/**
 * ArenaX Canonical Wordmark (Phase 118).
 *
 * The CEO-locked brand asset.  "Arena" in Montserrat ExtraBold (auto-themes:
 * white in dark, graphite in light) and "X" filled with the immutable
 * cyan→lime brand gradient (#00D3D9 → #A3FF4B).  Slightly larger X with
 * tight kerning + subtle overlap matches the source artifact at
 * /app/frontend/public/arenax-wordmark.png.
 *
 * Use this component EVERYWHERE the brand appears.  Do not roll your own
 * brand mark.
 *
 *   <ArenaxWordmark />              ← default, navbar-sized
 *   <ArenaxWordmark size="lg" />    ← hero / splash
 *   <ArenaxWordmark size="sm" />    ← footers / inline references
 *   <ArenaxWordmark withAttribution /> ← appends a tiny "by Out-Play"
 */
import React from 'react';

const SIZE_MAP = {
  xs: { font: '14px',  attribution: '8px' },
  sm: { font: '18px',  attribution: '9px' },
  md: { font: '22px',  attribution: '10px' },
  lg: { font: '34px',  attribution: '11px' },
  xl: { font: '56px',  attribution: '13px' },
};

export function ArenaxWordmark({
  size = 'md',
  withAttribution = false,
  className = '',
  ariaLabel = 'ArenaX',
}) {
  const { font, attribution } = SIZE_MAP[size] || SIZE_MAP.md;
  return (
    <span
      className={`arenax-wordmark inline-flex flex-col leading-none select-none ${className}`}
      aria-label={ariaLabel}
      role="img"
      data-testid="arenax-wordmark"
    >
      <span
        className="arenax-wordmark__lockup inline-flex items-baseline"
        style={{
          fontFamily: '"Montserrat", "Inter", system-ui, sans-serif',
          fontWeight: 800,
          letterSpacing: '-0.02em',
          fontSize: font,
          lineHeight: 1,
        }}
      >
        <span
          className="arenax-wordmark__arena"
          style={{
            color: `hsl(var(--ax-text))`,
            // Mild tracking-in on the last "a" to mimic the overlap with X.
            paddingRight: '0.02em',
          }}
        >
          Arena
        </span>
        <span
          className="arenax-wordmark__x"
          style={{
            background: 'var(--ax-brand-x-gradient)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
            // The X is slightly larger than the Arena letters and overlaps in
            // negative.  These nudges match the source artifact.
            fontSize: '1.08em',
            marginLeft: '-0.05em',
            transform: 'translateY(0.005em)',
            display: 'inline-block',
          }}
        >
          X
        </span>
      </span>
      {withAttribution && (
        <span
          className="arenax-wordmark__attribution mt-0.5 font-medium tracking-[0.25em] uppercase"
          style={{
            color: `hsl(var(--ax-text-faint))`,
            fontSize: attribution,
          }}
        >
          by Out-Play
        </span>
      )}
    </span>
  );
}

export default ArenaxWordmark;
